import {
  type DateParts,
  type DateSeg,
  formatPartsByPattern,
  parsePartsByPattern,
} from "./pattern";
import {
  getSiteTimeZone,
  instantFromSiteWallClock,
  siteWallClock,
  siteWeekday,
} from "./site-timezone";
import { formatLocaleOf, uiLanguageOf } from "@/lib/site/site-routing";

export {
  getSiteTimeZone,
  instantFromSiteWallClock,
  siteNowAsWallClockDate,
  siteWallClock,
  siteWeekday,
} from "./site-timezone";

export type TimeInput = string | number | Date | null | undefined;

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  build: (timeZone: string) => Intl.DateTimeFormat,
  locale = DEFAULT_DISPLAY_LOCALE,
): Intl.DateTimeFormat {
  const zone = getSiteTimeZone();
  const key = `${zone}|${locale}`;
  const hit = cache.get(key);

  if (hit) {
    return hit;
  }

  const created = build(zone);
  cache.set(key, created);

  return created;
}

const DEFAULT_DISPLAY_LOCALE = "zh-CN";

export function displayLocaleOf(uiLanguage: string): string {
  return formatLocaleOf(uiLanguageOf(uiLanguage));
}

function toDate(value: TimeInput): Date | null {
  if (value == null || value === "") {
    return null;
  }

  const date =
    value instanceof Date
      ? value
      : typeof value === "number"
        ? new Date(value)
        : toInstant(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>();

export function formatDateTime(value: TimeInput, uiLanguage?: string) {
  const locale = uiLanguage ? displayLocaleOf(uiLanguage) : DEFAULT_DISPLAY_LOCALE;
  const date = toDate(value);

  if (!date) {
    return "—";
  }

  return cachedFormatter(
    dateTimeCache,
    (timeZone) =>
      new Intl.DateTimeFormat(locale, {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    locale,
  ).format(date);
}

const dateTimeSecondsCache = new Map<string, Intl.DateTimeFormat>();

export function formatDateTimeSeconds(value: TimeInput, uiLanguage?: string) {
  const locale = uiLanguage ? displayLocaleOf(uiLanguage) : DEFAULT_DISPLAY_LOCALE;
  const date = toDate(value);

  if (!date) {
    return "—";
  }

  return cachedFormatter(
    dateTimeSecondsCache,
    (timeZone) =>
      new Intl.DateTimeFormat(locale, {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    locale,
  ).format(date);
}

const dateCache = new Map<string, Intl.DateTimeFormat>();

export function formatDate(value: TimeInput, uiLanguage?: string) {
  const locale = uiLanguage ? displayLocaleOf(uiLanguage) : DEFAULT_DISPLAY_LOCALE;
  const date = toDate(value);

  if (!date) {
    return "—";
  }

  return cachedFormatter(
    dateCache,
    (timeZone) =>
      new Intl.DateTimeFormat(locale, {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    locale,
  ).format(date);
}

const timeCache = new Map<string, Intl.DateTimeFormat>();

export function formatTime(value: TimeInput, uiLanguage?: string) {
  const locale = uiLanguage ? displayLocaleOf(uiLanguage) : DEFAULT_DISPLAY_LOCALE;
  const date = toDate(value);

  if (!date) {
    return "—";
  }

  return cachedFormatter(
    timeCache,
    (timeZone) =>
      new Intl.DateTimeFormat(locale, {
        timeZone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    locale,
  ).format(date);
}

const WEEKDAY_LABELS = [
  "星期日",
  "星期一",
  "星期二",
  "星期三",
  "星期四",
  "星期五",
  "星期六",
];

export function formatDateWithWeekday(value: TimeInput) {
  const date = toDate(value);

  if (!date) {
    return "—";
  }

  const parts = siteWallClock(date);

  return `${parts.year}年${padDatePart(parts.month)}月${padDatePart(parts.day)}日 ${WEEKDAY_LABELS[siteWeekday(date)]}`;
}

function siteCurrentYear(): number {
  return siteWallClock(new Date()).year;
}

function padDatePart(value: number, len = 2) {
  return String(value).padStart(len, "0");
}

function toLocalString(parts: DateParts) {
  return `${padDatePart(parts.year, 4)}-${padDatePart(parts.month)}-${padDatePart(parts.day)}T${padDatePart(parts.hour)}:${padDatePart(parts.minute)}:${padDatePart(parts.second)}`;
}

export function todayDatetimeLocalStart(date = new Date()) {
  const parts = siteWallClock(date);

  return toLocalString({ ...parts, hour: 0, minute: 0, second: 0 });
}

export function resolveDatetimeLocalStart(value: string | null | undefined) {
  const trimmed = value?.trim();

  if (!trimmed) {
    return todayDatetimeLocalStart();
  }

  const local = toDatetimeLocalValue(trimmed);

  return local || todayDatetimeLocalStart();
}

const ABSOLUTE_TIME = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const WALL_CLOCK =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

function toInstant(value: string): Date {
  const trimmed = value.trim();

  if (!ABSOLUTE_TIME.test(trimmed)) {
    const match = trimmed.match(WALL_CLOCK);

    if (match) {
      return instantFromSiteWallClock({
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4] ?? 0),
        minute: Number(match[5] ?? 0),
        second: Number(match[6] ?? 0),
      });
    }
  }

  return new Date(trimmed);
}

export function toDatetimeLocalValue(value: string | null | undefined) {
  const date = value ? toInstant(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return toLocalString(siteWallClock(date));
}

export function parseDatetimeLocalValue(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const date = toInstant(trimmed);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

export function formatInstantByPattern(
  value: string | null | undefined,
  segs: DateSeg[],
) {
  const trimmed = value?.trim();

  if (!trimmed) {
    return "";
  }

  const date = toInstant(trimmed);

  return Number.isNaN(date.getTime())
    ? ""
    : formatPartsByPattern(siteWallClock(date), segs);
}

export function parseInstantByPattern(value: string, segs: DateSeg[]) {
  const parts = parsePartsByPattern(value.trim(), segs, siteCurrentYear());

  if (!parts) {
    return null;
  }

  const date = instantFromSiteWallClock(parts);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
